import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { IsChallengeTriggeredResponse } from './models/IsChallengeTriggered.response';
import { IsUserLoggedResponse } from './models/IsUserLogged.response';
import { SocialLoginResponse } from './models/SocialLogin.response';
import { RegisterByEmailDto } from './models/registerByEmail.dto';
import { RegisterByGoogleDto } from './models/registerByGoogle.dto';
import { ResetPasswordError } from './models/errors';
import { CheckIfEmailExistsDto } from './models/checkIfEmailExists.dto';
import { catchResponseError } from '@portal-front/util-http-layer';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    }),
  };

  constructor(private http: HttpClient) {}

  login(email: string, password: string, rememberMe: boolean, recaptchaToken: string): Observable<any> {
    const params = new HttpParams({
      fromObject: {
        _username: email,
        _password: password,
        _accept_rules: 'on',
        ...(rememberMe ? { _remember_me: 'on' } : {}),
        'g-recaptcha-response': recaptchaToken,
      },
    });
    return this.http.post('/auth', params, this.httpOptions);
  }

  isChallengeTriggered(): Promise<boolean> {
    return firstValueFrom(
      this.http.get<IsChallengeTriggeredResponse>('/is-challenge-triggered').pipe(
        map((res) => res.isChallengeTriggered),
        catchError(() => of(false)),
      ),
    );
  }

  isLogged(): Promise<boolean> {
    return firstValueFrom(
      this.http.get<IsUserLoggedResponse>('/api/is-user-logged', this.httpOptions).pipe(
        map((res) => res.is_granted),
        catchError(() => of(false)),
      ),
    );
  }

  googleLogin(idToken: string): Promise<SocialLoginResponse> {
    const params = new HttpParams({
      fromObject: {
        token: idToken,
        type: 'google-id-token',
      },
    });
    return firstValueFrom(
      this.http.post('/social/login', params, this.httpOptions).pipe(
        map((res) => res as SocialLoginResponse),
        catchError(({ error, message }: HttpErrorResponse) =>
          of({
            success: false,
            code: 401,
            message: error?.message || message,
            token: idToken,
          } as SocialLoginResponse),
        ),
      ),
    );
  }

  public sendResetPasswordRequest(email: string, captchaToken: string): Promise<undefined> {
    const params = new HttpParams({
      fromObject: {
        email,
        'g-recaptcha-response': captchaToken,
      },
    });

    return firstValueFrom(
      this.http.post('/api/reset-password', params, this.httpOptions).pipe(
        map(() => undefined),
        catchResponseError(() => {
          throw new ResetPasswordError();
        }),
      ),
    );
  }

  public registerByEmail({
    email,
    acceptTerms,
    acceptMarketing,
    password,
    phoneNumber,
  }: RegisterByEmailDto): Observable<undefined> {
    const data = {
      email,
      acceptRules: acceptTerms,
      acceptRules1: acceptMarketing,
      invoice: false,
      password,
      repassword: password,
      phoneNumber: phoneNumber,
    };

    return this.http.post('/api/register', data).pipe(map(() => undefined));
  }

  public registerByGoogle(dto: RegisterByGoogleDto): Observable<undefined> {
    const params = new HttpParams({
      fromObject: {
        token: dto.googleToken,
        token_type: 'id_token',
        e_invoice_agree: dto.termsAccepted,
        marketing_agree: dto.marketingAccepted,
        phone_number: dto.phoneNumber ?? '',
      },
    });

    return this.http.post('/google-register', params, this.httpOptions).pipe(map(() => undefined));
  }

  public checkIfEmailExists(dto: CheckIfEmailExistsDto): Observable<undefined> {
    const result = this.http.get<undefined>(`/exists/email/${dto.email}/${dto.captchaToken}`);
    return result;
  }
}
